import type { FormGroup } from '../types/templateForms';

/*
 * A V2 intake form stores its groups in three places that must agree:
 *  - template_forms.form_groups: the group list (order, descriptions, empty groups). The client
 *    intake form shows only the groups in this list
 *  - questionSchema.formGroupTitle on each field: the group the builder shows the field in
 *  - template_form_fields.form_group_title on each field: the group the client intake form, PDFs
 *    and workflows use
 * The helpers below detect and repair disagreements. The builder's copy wins, since it is what the
 * admin sees.
 */

interface GroupedField {
    formGroupTitle?: string | null;
    questionSchema?: { formGroupTitle?: string | null } | null;
}

export function getFieldGroupTitle(field: GroupedField): string | null {
    return field.questionSchema?.formGroupTitle || field.formGroupTitle || null;
}

export function hasMismatchedGroupTitles(field: GroupedField): boolean {
    const groupTitle = getFieldGroupTitle(field);
    return (
        groupTitle !== null &&
        (field.formGroupTitle !== groupTitle || field.questionSchema?.formGroupTitle !== groupTitle)
    );
}

/**
 * Returns the field with both copies of its group title set to the builder's, or the same field
 * when they already agree.
 */
export function syncFieldGroupTitles<T extends GroupedField & { questionSchema: object }>(
    field: T,
): T {
    if (!hasMismatchedGroupTitles(field)) {
        return field;
    }

    const groupTitle = getFieldGroupTitle(field);
    return {
        ...field,
        formGroupTitle: groupTitle,
        questionSchema: { ...field.questionSchema, formGroupTitle: groupTitle },
    };
}

/**
 * Lists the groups the fields belong to, in the order they first appear.
 */
export function deriveFormGroupsFromFields(fields: GroupedField[]): FormGroup[] {
    const groupNames = new Set<string>();

    for (const field of fields) {
        const groupName = getFieldGroupTitle(field);
        if (groupName) {
            groupNames.add(groupName);
        }
    }

    return Array.from(groupNames).map((name, index) => ({
        name,
        displayOrder: index + 1,
    }));
}

/**
 * Returns the stored form groups plus any group a field belongs to that is missing from them,
 * appended at the end. Without stored groups, derives them from the fields.
 */
export function resolveFormGroups(
    formGroups: FormGroup[] | null | undefined,
    fields: GroupedField[],
): FormGroup[] {
    const derivedGroups = deriveFormGroupsFromFields(fields);
    if (!formGroups?.length) {
        return derivedGroups;
    }

    const existingNames = new Set(formGroups.map((group) => group.name));
    const missingGroups = derivedGroups.filter((group) => !existingNames.has(group.name));
    if (missingGroups.length === 0) {
        return formGroups;
    }

    const maxDisplayOrder = formGroups.reduce((max, group) => Math.max(max, group.displayOrder), 0);
    return [
        ...formGroups,
        ...missingGroups.map((group, index) => ({
            ...group,
            displayOrder: maxDisplayOrder + index + 1,
        })),
    ];
}

export interface FormGroupMismatches {
    // Groups fields belong to that the stored list is missing. Empty when no list is stored,
    // since every group is then derived from the fields
    missingGroupNames: string[];
    // Names of fields whose two group title copies disagree
    mismatchedFieldNames: string[];
}

export function findFormGroupMismatches(
    formGroups: FormGroup[] | null | undefined,
    fields: (GroupedField & { name: string })[],
): FormGroupMismatches {
    const missingGroupNames = formGroups?.length
        ? resolveFormGroups(formGroups, fields)
              .slice(formGroups.length)
              .map((group) => group.name)
        : [];

    return {
        missingGroupNames,
        mismatchedFieldNames: fields.filter(hasMismatchedGroupTitles).map((field) => field.name),
    };
}

export function hasFormGroupMismatches(mismatches: FormGroupMismatches): boolean {
    return mismatches.missingGroupNames.length > 0 || mismatches.mismatchedFieldNames.length > 0;
}
